const API_BASE = "/api/v1";

function getToken(): string {
  return (
    window.localStorage.getItem("intimsync.access_token") ||
    window.localStorage.getItem("admin_jwt") ||
    window.localStorage.getItem("jwt") ||
    window.localStorage.getItem("token") ||
    ""
  );
}

export async function request<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(init.body ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  console.debug("[groups api request]", {
    url: `${API_BASE}${path}`,
    hasToken: !!token,
    tokenStart: token ? token.slice(0, 20) : "",
    hasAuthHeader: !!headers.Authorization,
  });

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...headers,
      ...(init.headers as Record<string, string> | undefined),
    },
    credentials: "include",
  });

  const text = await res.text();
  let data: any = null;

  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { raw: text };
    }
  }

  if (!res.ok) {
    throw new Error(data?.error || data?.code || data?.message || `HTTP ${res.status}`);
  }

  return data as T;
}
export async function getGroup(groupId: string) {
  return request<any>(`/groups/${encodeURIComponent(groupId)}`);
}

export async function requestJoinGroup(groupId: string, message?: string) {
  return request<any>(`/groups/${encodeURIComponent(groupId)}/join-request`, {
    method: "POST",
    body: JSON.stringify({ message: message || "" }),
  });
}

export async function joinGroupWithCode(groupId: string, joinCode: string) {
  return request<any>(`/groups/${encodeURIComponent(groupId)}/join-with-code`, {
    method: "POST",
    body: JSON.stringify({ joinCode }),
  });
}

export async function createGroupInvite(
  groupId: string,
  payload: {
    invitedEmail?: string;
    invitedUserId?: number | string;
    expiresAt?: string | null;
  }
) {
  return request<any>(`/groups/${encodeURIComponent(groupId)}/invites`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function acceptGroupInvite(payload: {
  inviteId?: string;
  token?: string;
  inviteToken?: string;
}) {
  return request<any>("/group-invites/accept", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function bindGroupInviteEmail(email: string) {
  return request<any>("/group-invites/bind-email", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function listGroupJoinRequests(groupId: string) {
  return request<{ ok?: boolean; items?: any[] }>(
    `/groups/${encodeURIComponent(groupId)}/join-requests`
  );
}

export async function approveGroupJoinRequest(groupId: string, requestId: string) {
  return request<any>(
    `/groups/${encodeURIComponent(groupId)}/join-requests/${encodeURIComponent(requestId)}/approve`,
    {
      method: "POST",
      body: JSON.stringify({}),
    }
  );
}

export async function rejectGroupJoinRequest(groupId: string, requestId: string) {
  return request<any>(
    `/groups/${encodeURIComponent(groupId)}/join-requests/${encodeURIComponent(requestId)}/reject`,
    {
      method: "POST",
      body: JSON.stringify({}),
    }
  );
}


export async function leaveGroup(groupId: string) {
  return request<any>(`/groups/${encodeURIComponent(groupId)}/leave`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function listGroupMembers(groupId: string) {
  return request<{ ok?: boolean; items?: any[] }>(
    `/groups/${encodeURIComponent(groupId)}/members`
  );
}

export async function listGroupInvites(groupId: string) {
  return request<{ ok?: boolean; items?: any[] }>(
    `/groups/${encodeURIComponent(groupId)}/invites`
  );
}

export async function revokeGroupInvite(groupId: string, inviteId: string) {
  return request<any>(
    `/groups/${encodeURIComponent(groupId)}/invites/${encodeURIComponent(inviteId)}/revoke`,
    {
      method: "POST",
      body: JSON.stringify({}),
    }
  );
}

export async function listGroupTodos(groupId: string) {
  return request<{ ok?: boolean; items?: any[] }>(
    `/groups/${encodeURIComponent(groupId)}/todos`
  );
}

export async function createGroupTodo(
  groupId: string,
  payload: {
    title: string;
    description?: string;
    assignedUserId?: number | null;
    assignedUserIds?: Array<number | string>;
    dueAt?: string | null;
    location?: string;
    sortOrder?: number;
  }
) {
  return request<any>(`/groups/${encodeURIComponent(groupId)}/todos`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function listGroupActivities(groupId: string) {
  return request<{ ok?: boolean; items?: any[] }>(
    `/groups/${encodeURIComponent(groupId)}/activities`
  );
}

export async function createGroupActivity(
  groupId: string,
  payload: {
    title: string;
    description?: string;
    location?: string;
    startsAt?: string | null;
    endsAt?: string | null;
  }
) {
  return request<any>(`/groups/${encodeURIComponent(groupId)}/activities`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function joinGroupActivity(groupId: string, activityId: string) {
  return request<any>(
    `/groups/${encodeURIComponent(groupId)}/activities/${encodeURIComponent(activityId)}/join`,
    {
      method: "POST",
      body: JSON.stringify({}),
    }
  );
}


export async function leaveGroupActivity(groupId: string, activityId: string) {
  return request<any>(
    `/groups/${encodeURIComponent(groupId)}/activities/${encodeURIComponent(activityId)}/leave`,
    {
      method: "POST",
      body: JSON.stringify({}),
    }
  );
}

export async function getActivity(activityId: string) {
  return request<any>(`/activities/${encodeURIComponent(activityId)}`);
}

export async function getMessageContextSummary(payload: {
  originGroupId?: string | null;
  originActivityId?: string | null;
}) {
  const params = new URLSearchParams();

  if (payload.originGroupId) {
    params.set("originGroupId", payload.originGroupId);
  }

  if (payload.originActivityId) {
    params.set("originActivityId", payload.originActivityId);
  }

  return request<any>(`/context-summary?${params.toString()}`);
}


export async function createGroup(payload: {
  label: string;
  description?: string;
  groupType?: string;
  topicTags?: string | string[];
  postingPolicy?: string;
  visibility?: string;
  joinPolicy?: string;
  isDiscoverable?: boolean;
  isRecommendable?: boolean;
}) {
  return request<any>("/groups", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function listMyGroups() {
  return request<{ ok?: boolean; items?: any[] }>("/groups/mine");
}

export async function listRecommendedGroups() {
  return request<{ ok?: boolean; items?: any[] }>("/groups/recommended");
}

export async function listDiscoverableGroups(q?: string) {
  const suffix = q ? `?q=${encodeURIComponent(q)}` : "";
  return request<{ ok?: boolean; items?: any[] }>(`/groups/discoverable${suffix}`);
}

export async function updateGroupPrivacy(
  groupId: string,
  payload: {
    allowMemberPrivateMessages: boolean;
    isRecommendable: boolean;
  }
) {
  return request<any>(`/groups/${encodeURIComponent(groupId)}/privacy`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateMyGroupMemberPrivacy(
  groupId: string,
  payload: {
    allowContextDm: boolean;
  }
) {
  return request<any>(`/groups/${encodeURIComponent(groupId)}/my-member-privacy`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}


export async function reportGroup(groupId: string, payload: {
  reason: string;
  details?: string;
  targetUserId?: number | string | null;
  targetMessageId?: number | string | null;
}) {
  return request<any>(`/groups/${encodeURIComponent(groupId)}/report`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function reportUser(userId: string | number, payload: {
  reason: string;
  details?: string;
  originGroupId?: string | null;
  originActivityId?: string | null;
}) {
  return request<any>(`/users/${encodeURIComponent(String(userId))}/report`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateGroupPostingPolicy(groupId: string, postingPolicy: string) {
  return request<any>(`/groups/${encodeURIComponent(groupId)}/posting-policy`, {
    method: "POST",
    body: JSON.stringify({ postingPolicy }),
  });
}

export async function setGroupMemberRole(groupId: string, userId: string | number, roleName: string, reason?: string) {
  return request<any>(
    `/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(String(userId))}/role`,
    {
      method: "POST",
      body: JSON.stringify({ roleName, reason: reason || "" }),
    }
  );
}

export async function kickGroupMember(groupId: string, userId: string | number, reason?: string) {
  return request<any>(
    `/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(String(userId))}/kick`,
    {
      method: "POST",
      body: JSON.stringify({ reason: reason || "" }),
    }
  );
}

export async function banGroupMember(groupId: string, userId: string | number, reason?: string) {
  return request<any>(
    `/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(String(userId))}/ban`,
    {
      method: "POST",
      body: JSON.stringify({ reason: reason || "" }),
    }
  );
}

export async function ensureMyPartnerGroups() {
  return request<{ ok?: boolean; items?: any[] }>("/partner-groups/ensure-mine", {
    method: "POST",
    body: JSON.stringify({}),
  });
}
